import { motion } from 'framer-motion';
import { useState } from 'react';

type TechnologyItem = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
};

const technologyItems: TechnologyItem[] = [
  {
    id: 'quantum-core',
    title: 'Quantenprozessor',
    description: 'Unser leistungsstarker Quantenprozessor ermöglicht die Echtzeit-Verarbeitung komplexer holografischer Daten mit beispielloser Geschwindigkeit und Präzision.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: 'holographic-lens',
    title: 'Holografische Linse',
    description: 'Die fortschrittliche Linse projiziert kristallklare Hologramme mit einer Auflösung von über 8K und einem Sichtwinkel von 180 Grad.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
  },
  {
    id: 'projection-array',
    title: 'Projektions-Array',
    description: 'Ein mehrschichtiges Lasersystem erzeugt präzise Lichtfelder, die in der Luft schweben und mit bloßem Auge sichtbar sind.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    id: 'wireless-charging',
    title: 'Drahtlose Energie',
    description: 'Integrierte kabellose Energieübertragung ermöglicht kontinuierlichen Betrieb ohne Kabel, mit einer Akkulaufzeit von bis zu 12 Stunden.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: 'ai-processor',
    title: 'KI-Prozessor',
    description: 'Eine spezialisierte KI-Einheit optimiert in Echtzeit die holografische Darstellung und ermöglicht fortschrittliche Interaktionen.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'quantum-storage',
    title: 'Quantenspeicher',
    description: 'Revolutionärer Speicher mit nahezu unbegrenzter Kapazität für Ihre holografischen Projekte und Objekte.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
    ),
  },
];

const Technology = () => {
  const [activeTab, setActiveTab] = useState('quantum-core');
  const activeItem = technologyItems.find(item => item.id === activeTab) || technologyItems[0];

  return (
    <section id="technology" className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 -left-20 w-96 h-96 rounded-full bg-indigo-600 filter blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 rounded-full bg-purple-600 filter blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Revolutionäre{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-indigo-400">
              Technologie
            </span>
          </h2>
          <p className="text-lg text-gray-300">
            IHoloTool kombiniert Spitzentechnologien, um eine beispiellose holografische Erfahrung zu bieten.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Technology list */}
          <div className="w-full lg:w-1/3 space-y-4">
            {technologyItems.map((item) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                onClick={() => setActiveTab(item.id)}
                className={`w-full text-left p-6 rounded-2xl transition-all duration-300 ${
                  activeTab === item.id
                    ? 'bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 shadow-lg shadow-primary/10'
                    : 'bg-white/5 border border-white/5 hover:border-white/10'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                      activeTab === item.id
                        ? 'bg-primary/10 text-primary'
                        : 'bg-white/5 text-gray-400'
                    }`}
                  >
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    {item.title}
                  </h3>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Technology showcase */}
          <motion.div 
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-1 w-full"
          >
            <div className="glass-card p-8 h-full">
              <div className="relative aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden mb-8 border border-white/10">
                {/* 3D model or visualization would go here */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/20 to-transparent border border-primary/30 flex items-center justify-center">
                      {activeItem.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {activeItem.title}
                    </h3>
                  </div>
                </div>
                
                {/* Animated elements */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-primary/5 border border-primary/20 animate-pulse"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-primary/10 border border-primary/30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-primary/20 border border-primary/40 animate-pulse" style={{ animationDelay: '1s' }}></div>
              </div>
              
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 text-lg">
                  {activeItem.description}
                </p>
                
                {/* Tech specs */}
                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-800/50 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Leistung</div>
                    <div className="text-lg font-medium text-white">25 TFLOPS</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Effizienz</div>
                    <div className="text-lg font-medium text-white">95%+</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Latenz</div>
                    <div className="text-lg font-medium text-white">&lt;2ms</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Präzision</div>
                    <div className="text-lg font-medium text-white">0.01mm</div>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-white/5">
                  <h4 className="text-white font-medium mb-4">Technische Details</h4>
                  <ul className="space-y-2 text-gray-400">
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Quantenverschränkung für Echtzeit-Datenverarbeitung
                    </li>
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Mehrschichtige optische Beschichtung
                    </li>
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Passive Kühlung mit Graphen-Technologie
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Technology;
